import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  HiChartBar,
  HiUsers,
  HiCheckCircle,
  HiClock,
  HiXCircle,
  HiArrowUp,
  HiArrowDown,
  HiDocumentText,
  HiRocketLaunch,
  HiClipboardDocumentCheck,
  HiEye,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import { getAllSubmissions, canViewAll } from '../../services/commercialisationService';
import { normalizeRoleName } from '../../utils/roleResolver';
import { firestore } from '../../database/firebase';
import { collection, doc, getDoc, getDocs, query, where, onSnapshot } from 'firebase/firestore';

const AdminAnalyticsDashboard = () => {
  const { currentUser } = useAuth();
  const { userRole } = useUserRole();
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [analytics, setAnalytics] = useState({
    totalClients: 0,
    // Readiness Checklist
    readinessChecklist: {
      total: 0,
      readyToCommercialise: 0,
      almostReady: 0,
      notReady: 0,
      averageScore: 0,
      topGaps: [],
    },
    // GTM Plan
    gtmPlan: {
      total: 0,
      completed: 0,
      inProgress: 0,
      notStarted: 0,
      averageProgress: 0,
      averageStepsCompleted: 0,
      stepCompletion: {},
    },
    // Launch Checklist
    launchChecklist: {
      total: 0,
      ready: 0,
      almostReady: 0,
      notReady: 0,
      averageScore: 0,
      topGaps: [],
    },
    // Overall
    overall: {
      allModulesCompleted: 0,
      partialCompletion: 0,
      noCompletion: 0,
      averageOverallScore: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [readinessClients, setReadinessClients] = useState([]);
  const [selectedReadinessClient, setSelectedReadinessClient] = useState(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      // Check if user is a client - deny access
      const normalizedRole = normalizeRoleName(userRole);
      const rawRole = String(userRole || '').toLowerCase();
      const isBusinessAdvisor = rawRole.includes('business') || rawRole.includes('advisor');

      if (normalizedRole === 'Client') {
        setLoading(false);
        return;
      }

      // Set current user role and display name
      const displayRole = isBusinessAdvisor ? 'Business Advisor' : normalizedRole;
      setCurrentUserRole(displayRole);
      setUserDisplayName(currentUser.displayName || currentUser.email || 'User');

      if (!canViewAll(userRole) && !isBusinessAdvisor) {
        setLoading(false);
        return;
      }

      try {
        let submissions = [];
        let assignedClients = [];

        if (isBusinessAdvisor || normalizedRole === 'Business Advisor') {
          try {
            // Check if user has assignedClients in their profile
            const businessAdvisorQuery = query(
              collection(firestore, 'moderator'),
              where('email', '==', currentUser.email)
            );
            const advisorSnapshot = await getDocs(businessAdvisorQuery);

            if (!advisorSnapshot.empty) {
              const advisorData = advisorSnapshot.docs[0].data();
              assignedClients = advisorData.assignedClients || advisorData.assigned || [];
            }
            const moderatorQuery = query(
              collection(firestore, 'moderator'),
              where('email', '==', currentUser.email)
            );
            const moderatorSnapshot = await getDocs(moderatorQuery);

            if (!moderatorSnapshot.empty) {
              const moderatorData = moderatorSnapshot.docs[0].data();
              assignedClients = assignedClients.concat(
                moderatorData.assignedClients || moderatorData.assigned || []
              );
            }
          } catch (error) {}
        }

        // Get all submissions
        submissions = await getAllSubmissions(currentUser);

        // Filter submissions for Business Advisor based on assigned clients
        if (isBusinessAdvisor && assignedClients.length > 0) {
          // Filter by assigned client emails or UIDs
          submissions = submissions.filter(submission => {
            const userId = submission.userId;
            const userEmail = submission.userEmail || submission.userId;

            // Check if submission belongs to assigned client
            return assignedClients.some(assigned => {
              if (typeof assigned === 'string') {
                return (
                  assigned === userId ||
                  assigned === userEmail ||
                  assigned.toLowerCase() === userEmail?.toLowerCase()
                );
              }
              return assigned === userId || assigned === userEmail;
            });
          });
        }

        // Process analytics from submissions

        // Initialize statistics
        const stats = {
          totalClients: submissions.length,
          readinessChecklist: {
            total: 0,
            readyToCommercialise: 0,
            almostReady: 0,
            notReady: 0,
            scores: [],
            gapCounts: {},
          },
          gtmPlan: {
            total: 0,
            completed: 0,
            inProgress: 0,
            notStarted: 0,
            progressValues: [],
            stepsCompleted: [],
            stepCompletion: {},
          },
          launchChecklist: {
            total: 0,
            ready: 0,
            almostReady: 0,
            notReady: 0,
            scores: [],
            gapCounts: {},
          },
          overall: {
            allModulesCompleted: 0,
            partialCompletion: 0,
            noCompletion: 0,
            overallScores: [],
          },
        };

        const readinessRows = [];

        submissions.forEach(submission => {
          let modulesCompleted = 0;
          let totalScore = 0;
          let scoreCount = 0;

          // Readiness Checklist Analytics
          if (submission.readinessChecklist) {
            stats.readinessChecklist.total++;
            modulesCompleted++;
            const score = submission.readinessChecklist.score || 0;
            stats.readinessChecklist.scores.push(score);
            totalScore += score;
            scoreCount++;

            if (score >= 85) {
              stats.readinessChecklist.readyToCommercialise++;
            } else if (score >= 60) {
              stats.readinessChecklist.almostReady++;
            } else {
              stats.readinessChecklist.notReady++;
            }

            // Collect gaps
            if (submission.readinessChecklist.categoryScores) {
              Object.entries(submission.readinessChecklist.categoryScores).forEach(
                ([category, catScore]) => {
                  if (catScore.percentage < 60) {
                    stats.readinessChecklist.gapCounts[category] =
                      (stats.readinessChecklist.gapCounts[category] || 0) + 1;
                  }
                }
              );
            }

            const readinessStatus =
              score >= 85 ? 'Ready' : score >= 60 ? 'Almost Ready' : 'Not Ready';
            readinessRows.push({
              userId: submission.userId,
              email: submission.clientEmail || submission.userEmail || '',
              name: submission.clientName || submission.userEmail || 'Unknown client',
              score,
              status: readinessStatus,
              totalChecked: submission.readinessChecklist.totalChecked ?? null,
              totalItems: submission.readinessChecklist.totalItems ?? null,
              categoryScores: submission.readinessChecklist.categoryScores || {},
              updatedAt:
                submission.readinessChecklist.updatedAt ||
                submission.readinessChecklist.lastUpdatedAt ||
                null,
            });
          }

          // GTM Plan Analytics
          if (submission.gtmPlan) {
            stats.gtmPlan.total++;
            modulesCompleted++;
            const progress = submission.gtmPlan.progress || 0;
            const currentStep = submission.gtmPlan.currentStep || 0;
            const totalSteps = submission.gtmPlan.steps?.length || 15;

            stats.gtmPlan.progressValues.push(progress);
            stats.gtmPlan.stepsCompleted.push(currentStep);

            if (progress >= 100 || currentStep >= totalSteps) {
              stats.gtmPlan.completed++;
            } else if (currentStep > 0) {
              stats.gtmPlan.inProgress++;
            } else {
              stats.gtmPlan.notStarted++;
            }

            // Track step completion
            if (submission.gtmPlan.steps) {
              submission.gtmPlan.steps.forEach((step, index) => {
                if (!stats.gtmPlan.stepCompletion[index]) {
                  stats.gtmPlan.stepCompletion[index] = { completed: 0, total: 0 };
                }
                stats.gtmPlan.stepCompletion[index].total++;
                if (step.completed) {
                  stats.gtmPlan.stepCompletion[index].completed++;
                }
              });
            }
          }

          // Launch Checklist Analytics
          if (submission.launchChecklist) {
            stats.launchChecklist.total++;
            modulesCompleted++;
            const score = submission.launchChecklist.score || 0;
            stats.launchChecklist.scores.push(score);
            totalScore += score;
            scoreCount++;

            if (score >= 85) {
              stats.launchChecklist.ready++;
            } else if (score >= 60) {
              stats.launchChecklist.almostReady++;
            } else {
              stats.launchChecklist.notReady++;
            }

            // Collect gaps
            if (submission.launchChecklist.categoryScores) {
              Object.entries(submission.launchChecklist.categoryScores).forEach(
                ([category, catScore]) => {
                  if (catScore.percentage < 60) {
                    stats.launchChecklist.gapCounts[category] =
                      (stats.launchChecklist.gapCounts[category] || 0) + 1;
                  }
                }
              );
            }
          }

          // Overall completion tracking
          if (modulesCompleted === 3) {
            stats.overall.allModulesCompleted++;
          } else if (modulesCompleted > 0) {
            stats.overall.partialCompletion++;
          } else {
            stats.overall.noCompletion++;
          }

          if (scoreCount > 0) {
            stats.overall.overallScores.push(totalScore / scoreCount);
          }
        });

        // Calculate averages and format top gaps
        const readinessAverage =
          stats.readinessChecklist.scores.length > 0
            ? Math.round(
                stats.readinessChecklist.scores.reduce((a, b) => a + b, 0) /
                  stats.readinessChecklist.scores.length
              )
            : 0;

        const gtmAverageProgress =
          stats.gtmPlan.progressValues.length > 0
            ? Math.round(
                stats.gtmPlan.progressValues.reduce((a, b) => a + b, 0) /
                  stats.gtmPlan.progressValues.length
              )
            : 0;

        const gtmAverageSteps =
          stats.gtmPlan.stepsCompleted.length > 0
            ? Math.round(
                stats.gtmPlan.stepsCompleted.reduce((a, b) => a + b, 0) /
                  stats.gtmPlan.stepsCompleted.length
              )
            : 0;

        const launchAverage =
          stats.launchChecklist.scores.length > 0
            ? Math.round(
                stats.launchChecklist.scores.reduce((a, b) => a + b, 0) /
                  stats.launchChecklist.scores.length
              )
            : 0;

        const overallAverage =
          stats.overall.overallScores.length > 0
            ? Math.round(
                stats.overall.overallScores.reduce((a, b) => a + b, 0) /
                  stats.overall.overallScores.length
              )
            : 0;

        // Format top gaps for readiness checklist
        const readinessTopGaps = Object.entries(stats.readinessChecklist.gapCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([category, count]) => ({
            category: category.replace(/([A-Z])/g, ' $1').trim(),
            count,
            percentage: Math.round((count / stats.readinessChecklist.total) * 100),
          }));

        // Format top gaps for launch checklist
        const launchTopGaps = Object.entries(stats.launchChecklist.gapCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([category, count]) => ({
            category: category.replace(/([A-Z])/g, ' $1').trim(),
            count,
            percentage: Math.round((count / stats.launchChecklist.total) * 100),
          }));

        // Format step completion for GTM
        const stepCompletionFormatted = Object.entries(stats.gtmPlan.stepCompletion)
          .map(([step, data]) => ({
            step: parseInt(step) + 1,
            completed: data.completed,
            total: data.total,
            percentage: Math.round((data.completed / data.total) * 100),
          }))
          .sort((a, b) => b.percentage - a.percentage)
          .slice(0, 5);

        setAnalytics({
          totalClients: stats.totalClients,
          readinessChecklist: {
            ...stats.readinessChecklist,
            averageScore: readinessAverage,
            topGaps: readinessTopGaps,
          },
          gtmPlan: {
            ...stats.gtmPlan,
            averageProgress: gtmAverageProgress,
            averageStepsCompleted: gtmAverageSteps,
            stepCompletion: stepCompletionFormatted,
          },
          launchChecklist: {
            ...stats.launchChecklist,
            averageScore: launchAverage,
            topGaps: launchTopGaps,
          },
          overall: {
            ...stats.overall,
            averageOverallScore: overallAverage,
          },
        });
        setReadinessClients(
          readinessRows.sort((a, b) => {
            if (b.score === a.score) {
              return (a.name || '').localeCompare(b.name || '');
            }
            return b.score - a.score;
          })
        );
      } catch (error) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [currentUser, userRole]);

  // Check if user is a client - deny access
  const normalizedRole = userRole ? normalizeRoleName(userRole) : null;
  const rawRole = String(userRole || '').toLowerCase();
  const isBusinessAdvisor = rawRole.includes('business') || rawRole.includes('advisor');

  if (normalizedRole === 'Client') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">Access Denied</p>
          <p className="mt-2 text-sm text-gray-600">
            Clients do not have access to the analytics dashboard. Please contact your advisor or
            coach for assistance.
          </p>
        </div>
      </div>
    );
  }
  if (!canViewAll(userRole) && !isBusinessAdvisor) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">Access Denied</p>
          <p className="mt-2 text-sm text-gray-600">
            You need admin, coach, or business advisor privileges to view this dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h1 className="mb-2 text-3xl font-bold text-black">Commercialisation Analytics</h1>
                <p className="text-gray-600">
                  Comprehensive overview of all commercialisation modules and client progress
                </p>
              </div>
              {currentUserRole && (
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 shadow-sm">
                  <p className="text-xs font-medium text-gray-500">Viewing as</p>
                  <p className="text-sm font-semibold capitalize text-gray-900">
                    {currentUserRole}
                  </p>
                  {userDisplayName && <p className="text-xs text-gray-600">{userDisplayName}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Overall Key Metrics */}
          <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Clients</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{analytics.totalClients}</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                  <HiUsers className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">All Modules Completed</p>
                  <p className="mt-2 text-3xl font-bold text-green-600">
                    {analytics.overall.allModulesCompleted}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Avg Score: {analytics.overall.averageOverallScore}%
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <HiCheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Partial Completion</p>
                  <p className="mt-2 text-3xl font-bold text-yellow-600">
                    {analytics.overall.partialCompletion}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                  <HiClock className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">No Completion</p>
                  <p className="mt-2 text-3xl font-bold text-red-600">
                    {analytics.overall.noCompletion}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <HiXCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Module Comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3"
          >
            {/* Readiness Checklist */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <HiClipboardDocumentCheck className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Readiness Checklist</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Total Submissions</span>
                    <span className="font-medium text-gray-900">
                      {analytics.readinessChecklist.total}
                    </span>
                  </div>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Average Score</span>
                    <span className="font-medium text-gray-900">
                      {analytics.readinessChecklist.averageScore}%
                    </span>
                  </div>
                </div>
                <div className="space-y-2 border-t border-gray-200 pt-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-green-600">Ready</span>
                    <span className="font-medium">
                      {analytics.readinessChecklist.readyToCommercialise}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-yellow-600">Almost Ready</span>
                    <span className="font-medium">{analytics.readinessChecklist.almostReady}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-red-600">Not Ready</span>
                    <span className="font-medium">{analytics.readinessChecklist.notReady}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* GTM Plan */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <HiDocumentText className="h-5 w-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">GTM Strategy Plan</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Total Submissions</span>
                    <span className="font-medium text-gray-900">{analytics.gtmPlan.total}</span>
                  </div>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Average Progress</span>
                    <span className="font-medium text-gray-900">
                      {analytics.gtmPlan.averageProgress}%
                    </span>
                  </div>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Avg Steps Completed</span>
                    <span className="font-medium text-gray-900">
                      {analytics.gtmPlan.averageStepsCompleted}/15
                    </span>
                  </div>
                </div>
                <div className="space-y-2 border-t border-gray-200 pt-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-green-600">Completed</span>
                    <span className="font-medium">{analytics.gtmPlan.completed}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-yellow-600">In Progress</span>
                    <span className="font-medium">{analytics.gtmPlan.inProgress}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-red-600">Not Started</span>
                    <span className="font-medium">{analytics.gtmPlan.notStarted}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Launch Checklist */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <HiRocketLaunch className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">Launch Checklist</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Total Submissions</span>
                    <span className="font-medium text-gray-900">
                      {analytics.launchChecklist.total}
                    </span>
                  </div>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Average Score</span>
                    <span className="font-medium text-gray-900">
                      {analytics.launchChecklist.averageScore}%
                    </span>
                  </div>
                </div>
                <div className="space-y-2 border-t border-gray-200 pt-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-green-600">Ready</span>
                    <span className="font-medium">{analytics.launchChecklist.ready}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-yellow-600">Almost Ready</span>
                    <span className="font-medium">{analytics.launchChecklist.almostReady}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-red-600">Not Ready</span>
                    <span className="font-medium">{analytics.launchChecklist.notReady}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Readiness Checklist Details */}
          {analytics.readinessChecklist.total > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="mb-4 flex items-center gap-2">
                <HiClipboardDocumentCheck className="h-5 w-5 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Readiness Checklist Overview
                </h2>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">Ready to Commercialise</span>
                    <span className="text-gray-600">
                      {analytics.readinessChecklist.readyToCommercialise} clients
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.readinessChecklist.total > 0
                            ? (analytics.readinessChecklist.readyToCommercialise /
                                analytics.readinessChecklist.total) *
                              100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-green-600"
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">Almost Ready</span>
                    <span className="text-gray-600">
                      {analytics.readinessChecklist.almostReady} clients
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.readinessChecklist.total > 0
                            ? (analytics.readinessChecklist.almostReady /
                                analytics.readinessChecklist.total) *
                              100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-yellow-600"
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">Not Ready</span>
                    <span className="text-gray-600">
                      {analytics.readinessChecklist.notReady} clients
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.readinessChecklist.total > 0
                            ? (analytics.readinessChecklist.notReady /
                                analytics.readinessChecklist.total) *
                              100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-red-600"
                    />
                  </div>
                </div>
              </div>
              {analytics.readinessChecklist.topGaps.length > 0 && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h3 className="mb-4 text-sm font-semibold text-gray-900">Top 5 Readiness Gaps</h3>
                  <div className="space-y-3">
                    {analytics.readinessChecklist.topGaps.map((gap, index) => (
                      <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">{gap.category}</span>
                          <span className="text-xs font-medium text-gray-600">
                            {gap.count} clients ({gap.percentage}%)
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="h-full bg-red-600"
                            style={{ width: `${gap.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* GTM Plan Details */}
          {analytics.gtmPlan.total > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="mb-4 flex items-center gap-2">
                <HiDocumentText className="h-5 w-5 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-900">GTM Strategy Plan Overview</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">Completed</span>
                    <span className="text-gray-600">{analytics.gtmPlan.completed} clients</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.gtmPlan.total > 0
                            ? (analytics.gtmPlan.completed / analytics.gtmPlan.total) * 100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-green-600"
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">In Progress</span>
                    <span className="text-gray-600">{analytics.gtmPlan.inProgress} clients</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.gtmPlan.total > 0
                            ? (analytics.gtmPlan.inProgress / analytics.gtmPlan.total) * 100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-yellow-600"
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">Not Started</span>
                    <span className="text-gray-600">{analytics.gtmPlan.notStarted} clients</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.gtmPlan.total > 0
                            ? (analytics.gtmPlan.notStarted / analytics.gtmPlan.total) * 100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-red-600"
                    />
                  </div>
                </div>
              </div>
              {analytics.gtmPlan.stepCompletion.length > 0 && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h3 className="mb-4 text-sm font-semibold text-gray-900">
                    Top 5 Most Completed Steps
                  </h3>
                  <div className="space-y-3">
                    {analytics.gtmPlan.stepCompletion.map((step, index) => (
                      <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            Step {step.step}
                          </span>
                          <span className="text-xs font-medium text-gray-600">
                            {step.completed}/{step.total} ({step.percentage}%)
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="h-full bg-purple-600"
                            style={{ width: `${step.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Launch Checklist Details */}
          {analytics.launchChecklist.total > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="mb-4 flex items-center gap-2">
                <HiRocketLaunch className="h-5 w-5 text-indigo-600" />
                <h2 className="text-xl font-semibold text-gray-900">Launch Checklist Overview</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">Ready to Launch</span>
                    <span className="text-gray-600">{analytics.launchChecklist.ready} clients</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.launchChecklist.total > 0
                            ? (analytics.launchChecklist.ready / analytics.launchChecklist.total) *
                              100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-green-600"
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">Almost Ready</span>
                    <span className="text-gray-600">
                      {analytics.launchChecklist.almostReady} clients
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.launchChecklist.total > 0
                            ? (analytics.launchChecklist.almostReady /
                                analytics.launchChecklist.total) *
                              100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-yellow-600"
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">Not Ready</span>
                    <span className="text-gray-600">
                      {analytics.launchChecklist.notReady} clients
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          analytics.launchChecklist.total > 0
                            ? (analytics.launchChecklist.notReady /
                                analytics.launchChecklist.total) *
                              100
                            : 0
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-red-600"
                    />
                  </div>
                </div>
              </div>
              {analytics.launchChecklist.topGaps.length > 0 && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h3 className="mb-4 text-sm font-semibold text-gray-900">Top 5 Launch Gaps</h3>
                  <div className="space-y-3">
                    {analytics.launchChecklist.topGaps.map((gap, index) => (
                      <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">{gap.category}</span>
                          <span className="text-xs font-medium text-gray-600">
                            {gap.count} clients ({gap.percentage}%)
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="h-full bg-red-600"
                            style={{ width: `${gap.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {readinessClients.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HiClipboardDocumentCheck className="h-5 w-5 text-blue-600" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Readiness Checklist Completions
                  </h2>
                </div>
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                  {readinessClients.length} clients
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Score
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Last Updated
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {readinessClients.map(client => {
                      const updated =
                        client.updatedAt?.toDate?.() ||
                        (client.updatedAt ? new Date(client.updatedAt) : null);
                      const formattedDate = updated
                        ? updated.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : '—';
                      return (
                        <tr key={client.userId}>
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                            {client.name}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                            {client.email || '—'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-gray-900">
                            {client.score}%
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                client.status === 'Ready'
                                  ? 'bg-green-100 text-green-700'
                                  : client.status === 'Almost Ready'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {client.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                            {formattedDate}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                            <button
                              onClick={() => setSelectedReadinessClient(client)}
                              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:border-blue-300 hover:text-blue-700"
                            >
                              <HiEye className="h-4 w-4" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {selectedReadinessClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedReadinessClient.name}
                </h3>
                <p className="text-xs text-gray-500">{selectedReadinessClient.email}</p>
              </div>
              <button
                onClick={() => setSelectedReadinessClient(null)}
                className="rounded-full border border-gray-200 p-1 text-gray-500 transition hover:border-gray-300 hover:text-gray-700"
              >
                <HiXCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700">Overall Score</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {selectedReadinessClient.score}%
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                  <span>Status</span>
                  <span>{selectedReadinessClient.status}</span>
                </div>
                {selectedReadinessClient.totalItems != null && (
                  <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                    <span>Items Completed</span>
                    <span>
                      {selectedReadinessClient.totalChecked ?? 0} of{' '}
                      {selectedReadinessClient.totalItems}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <h4 className="mb-2 text-sm font-semibold text-gray-900">Category Breakdown</h4>
                <div className="space-y-2">
                  {Object.entries(selectedReadinessClient.categoryScores || {}).map(
                    ([category, data]) => (
                      <div
                        key={category}
                        className="rounded-lg border border-gray-200 bg-white p-3 text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">
                            {category.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className="font-semibold text-gray-900">
                            {data.percentage || 0}%
                          </span>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className={`h-full ${
                              data.percentage >= 85
                                ? 'bg-green-500'
                                : data.percentage >= 60
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                            }`}
                            style={{ width: `${data.percentage || 0}%` }}
                          />
                        </div>
                      </div>
                    )
                  )}
                  {Object.keys(selectedReadinessClient.categoryScores || {}).length === 0 && (
                    <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-500">
                      No category detail captured for this submission.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default AdminAnalyticsDashboard;
